import { useState } from "react"

import { Columns, CheckCircle, XCircle, ArrowCounterClockwise } from "@phosphor-icons/react"

import { Button } from "~/components/reusable/Button"
import { Checkbox } from "~/components/reusable/Checkbox"
import {
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { useDisabledColumnIdSet } from "~/hooks/useDisabledColumns"
import { formatName } from "~/utils/namingUtils"

export type CustomerTableColumnId =
  | "lastYearSales"
  | "totalPreorders"
  | "incomingPo"
  | "salesYtd"
  | "salesSamePeriodLastYear"
  | "daysSinceLastOrder"
  | "avgOrderFrequencyDays"
  | "avgOrderQuantity"
  | "orderFrequencyVariance"
  | "reorderLikelihood"
  | "country"
  | "city"
  | "zipCode"
  | "customerId"
  | "customerType"
  | "preOrderHandling"
  | "suggestedOrder"
  | "tags"

function getCustomerTableColumns(): { id: CustomerTableColumnId; label: string }[] {
  return [
    { id: "lastYearSales", label: formatName("sale", true, true) },
    { id: "totalPreorders", label: formatName("preorder", true, true) },
    { id: "incomingPo", label: "Incoming PO" },
    { id: "suggestedOrder", label: "Suggested Order" },
    { id: "salesYtd", label: `${formatName("sale", false, true)} YTD` },
    { id: "salesSamePeriodLastYear", label: `${formatName("sale", false, true)} same period LY` },
    { id: "daysSinceLastOrder", label: "Days since last order" },
    { id: "avgOrderFrequencyDays", label: "Avg order frequency" },
    { id: "avgOrderQuantity", label: "Avg order quantity" },
    { id: "orderFrequencyVariance", label: "Order freq. variance" },
    { id: "reorderLikelihood", label: "Reorder likelihood" },
    { id: "country", label: "Country" },
    { id: "city", label: "City" },
    { id: "zipCode", label: "Zip Code" },
    { id: "customerId", label: "Customer ID" },
    { id: "customerType", label: "Customer type" },
    { id: "preOrderHandling", label: `${formatName("preorder", false, true)} handling` },
    { id: "tags", label: "Tags" },
  ]
}

export const CUSTOMER_TABLE_ALL_COLUMNS: CustomerTableColumnId[] = [
  "lastYearSales",
  "totalPreorders",
  "incomingPo",
  "suggestedOrder",
  "salesYtd",
  "salesSamePeriodLastYear",
  "daysSinceLastOrder",
  "avgOrderFrequencyDays",
  "avgOrderQuantity",
  "orderFrequencyVariance",
  "reorderLikelihood",
  "country",
  "city",
  "zipCode",
  "customerId",
  "customerType",
  "preOrderHandling",
  "tags",
]

export const CUSTOMER_TABLE_DEFAULT_COLUMNS: CustomerTableColumnId[] = [
  "lastYearSales",
  "totalPreorders",
  "incomingPo",
  "suggestedOrder",
  "salesYtd",
  "salesSamePeriodLastYear",
  "daysSinceLastOrder",
  "avgOrderFrequencyDays",
  "avgOrderQuantity",
  "reorderLikelihood",
  "customerId",
  "customerType",
  "preOrderHandling",
]

type Props = {
  availableColumnIds: CustomerTableColumnId[]
  displayedColumnIds: string[]
  setDisplayedColumnIds: (ids: string[]) => void
}

export default function CustomerColumnChooser({
  availableColumnIds,
  displayedColumnIds,
  setDisplayedColumnIds,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [local, setLocal] = useState<string[]>([])

  // Columns the company has turned off in settings are not offered in the picker.
  const disabledColumnIds = useDisabledColumnIdSet()
  const enabledColumnIds = availableColumnIds.filter((id) => !disabledColumnIds.has(id))

  const handleOpenChange = (open: boolean) => {
    if (open) setLocal(displayedColumnIds)
    if (!open && isOpen) {
      const hasChanges =
        local.length !== displayedColumnIds.length ||
        !local.every((id) => displayedColumnIds.includes(id))
      if (hasChanges) setDisplayedColumnIds(local)
    }
    setIsOpen(open)
  }

  const toggle = (id: string) => {
    setLocal((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const columns = getCustomerTableColumns().filter((c) => enabledColumnIds.includes(c.id))

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="neutral" size="sm">
          <Columns />
          {displayedColumnIds.length}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Customize Columns</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          <Button variant="transparent" size="sm" onClick={() => setLocal(enabledColumnIds)}>
            <CheckCircle />
            Select all
          </Button>
          <Button variant="transparent" size="sm" onClick={() => setLocal([])}>
            <XCircle />
            Clear
          </Button>
          <Button
            variant="transparent"
            size="sm"
            onClick={() =>
              setLocal(CUSTOMER_TABLE_DEFAULT_COLUMNS.filter((id) => !disabledColumnIds.has(id)))
            }
          >
            <ArrowCounterClockwise />
            Reset to default
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {columns.map((col) => (
            <label
              key={col.id}
              className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 -mx-2"
            >
              <Checkbox checked={local.includes(col.id)} onCheckedChange={() => toggle(col.id)} />
              <span className="text-sm">{col.label}</span>
            </label>
          ))}
        </div>
      </DialogContent>
    </DialogRoot>
  )
}
